import { z } from 'zod';
import type { ChatContextRef } from '@govard/ai-chat-kit';
import { crmRu } from '../../i18n/ru/crm.js';
import { featuresRu } from '../../i18n/ru/features.js';
import { crmEn } from '../../i18n/en/crm.js';
import { featuresEn } from '../../i18n/en/features.js';
const commandCatalogs = { ru: { ...crmRu, ...featuresRu }, en: { ...crmEn, ...featuresEn } };
import { UI_COMMANDS, type UiCommandKey } from './result-action-commands.js';
import { SIMPLE_PLAN_COMMAND_SCHEMAS } from '../broker/simple-plan.js';

const id = z.string().trim().min(1).max(200);
const ids = z
  .array(id)
  .min(1)
  .max(7)
  .refine((values) => new Set(values).size === values.length);
const target = { dealId: id.optional(), contactId: id.optional(), projectId: id.optional() };
const empty = z.object({}).strict();
const entity = z.object(target).strict();
const deal = z.object({ dealId: id }).strict();
const source = z.object({ sourceMessageId: id }).strict();
const selection = z.object({ sourceMessageId: id, dealId: id, selectedActionIds: ids }).strict();
const dashboardSource = z.object({ kind: z.literal('dashboard_briefing') }).strict();
function briefingSelection(field: 'taskIds' | 'dealIds' | 'meetingIds') {
  const selected =
    field === 'meetingIds'
      ? z
          .array(id)
          .min(1)
          .max(20)
          .refine((values) => new Set(values).size === values.length)
      : ids;
  return z.union([
    z.object({ sourceMessageId: id, [field]: selected }).strict(),
    z.object({ source: dashboardSource, [field]: ids }).strict(),
  ]);
}

function definition<Schema extends z.ZodType>(
  schema: Schema,
  presentationKey: string,
  instructionKey: string,
) {
  return {
    schema,
    presentationKey,
    // The instruction is a server-owned bridge, never a client label.
    handler: (params: Record<string, unknown>) => {
      let key = instructionKey;
      if (instructionKey === 'crm.ai_chat.prompt.hot_leads' && params.dealIds !== undefined)
        key = 'crm.ai_chat.prompt.my_day_hot_leads';
      if (instructionKey === 'crm.ai_chat.prompt.follow_up' && params.dealId !== undefined)
        key = 'crm.chat.quick_follow_up_prompt';
      if (params.projectId !== undefined && instructionKey === 'crm.ai_chat.prompt.follow_up')
        key = 'crm.projects.copilot.suggestion_changes_prompt';
      if (
        params.projectId !== undefined &&
        instructionKey === 'crm.chat.suggestion_next_step_prompt'
      )
        key = 'crm.projects.copilot.suggestion_next_steps_prompt';
      if (params.contactId !== undefined && instructionKey === 'crm.ai_chat.prompt.follow_up')
        key = 'crm.features.dynamic.features.ai.chat.components.chatpanel.7';
      if (
        params.contactId !== undefined &&
        instructionKey === 'crm.chat.suggestion_next_step_prompt'
      )
        key = 'crm.features.dynamic.features.ai.chat.components.chatpanel.10';
      if (params.contactId !== undefined && instructionKey === 'crm.chat.suggestion_risks_prompt')
        key = 'crm.features.dynamic.features.ai.chat.components.chatpanel.11';
      return catalogText('ru', key, {
        ...params,
        p0: params.contactId ?? params.dealId,
        p1: params.dealId,
      });
    },
  };
}

export const UI_COMMAND_REGISTRY = {
  create_task: definition(
    SIMPLE_PLAN_COMMAND_SCHEMAS.create_task,
    'crm.chat.simple.create_task',
    'crm.chat.simple.create_task',
  ),
  update_task: definition(
    SIMPLE_PLAN_COMMAND_SCHEMAS.update_task,
    'crm.chat.simple.update_task',
    'crm.chat.simple.update_task',
  ),
  create_deal: definition(
    SIMPLE_PLAN_COMMAND_SCHEMAS.create_deal,
    'crm.chat.simple.create_deal',
    'crm.chat.simple.create_deal',
  ),
  search_contacts: definition(
    SIMPLE_PLAN_COMMAND_SCHEMAS.search_contacts,
    'crm.chat.simple.search_contacts',
    'crm.chat.simple.search_contacts',
  ),
  read_contact: definition(
    SIMPLE_PLAN_COMMAND_SCHEMAS.read_contact,
    'crm.chat.simple.read_contact',
    'crm.chat.simple.read_contact',
  ),
  search_companies: definition(
    SIMPLE_PLAN_COMMAND_SCHEMAS.search_companies,
    'crm.chat.simple.search_companies',
    'crm.chat.simple.search_companies',
  ),
  read_company: definition(
    SIMPLE_PLAN_COMMAND_SCHEMAS.read_company,
    'crm.chat.simple.read_company',
    'crm.chat.simple.read_company',
  ),
  undo_created_task: definition(
    z.object({ sourceApprovalId: id }).strict(),
    'crm.approvals.undo_created_task_label',
    'crm.approvals.undo_created_task_label',
  ),
  create_note_from_artifact: definition(
    z
      .object({ sourceMessageId: id, artifactIndex: z.number().int().min(0).max(19), dealId: id })
      .strict(),
    'crm.ai_chat.artifact.attach_note',
    'crm.ai_chat.artifact.action_note_prefix',
  ),
  create_tasks_from_artifact: definition(
    z
      .object({
        sourceMessageId: id,
        artifactIndex: z.number().int().min(0).max(19),
        dealId: id.optional(),
      })
      .strict(),
    'crm.ai_chat.artifact.create_crm_tasks',
    'crm.ai_chat.artifact.action_tasks_prefix',
  ),
  crm_list_next_page: definition(
    source,
    'crm.chat.crm_list.next_page',
    'crm.chat.crm_list.next_page_prompt',
  ),
  create_tasks_from_answer: definition(
    z.object({ sourceMessageId: id, dealId: id.optional() }).strict(),
    'crm.ai_chat.actions.create_tasks',
    'crm.ai_chat.actions.create_tasks_prompt',
  ),
  add_deal_note: definition(
    z.object({ sourceMessageId: id, dealId: id }).strict(),
    'crm.ai_chat.actions.pin_note',
    'crm.ai_chat.actions.pin_note_prompt',
  ),
  dashboard_widget_from_answer: definition(
    source,
    'crm.ai_chat.actions.dashboard',
    'crm.ai_chat.actions.dashboard_prompt',
  ),
  manual_deal_note: definition(
    z.object({ dealId: id, body: z.string().trim().min(1).max(4000) }).strict(),
    'crm.chat.crud_add_note',
    'crm.chat.crud_add_note',
  ),
  update_deal_stage: definition(
    z
      .object({ dealId: id, stage: z.enum(['New', 'Contacted', 'Negotiation', 'Won', 'Lost']) })
      .strict(),
    'crm.chat.crud_update_stage',
    'crm.chat.crud_update_stage',
  ),
  quick_follow_up: definition(entity, 'crm.chat.quick_follow_up', 'crm.ai_chat.prompt.follow_up'),
  cockpit: definition(deal, 'crm.chat.suggestion_cockpit', 'crm.chat.suggestion_cockpit_prompt'),
  next_step: definition(
    entity,
    'crm.chat.suggestion_next_step',
    'crm.chat.suggestion_next_step_prompt',
  ),
  risks: definition(entity, 'crm.chat.suggestion_risks', 'crm.chat.suggestion_risks_prompt'),
  changes: definition(
    z.object({ projectId: id }).strict(),
    'crm.projects.copilot.suggestion_changes',
    'crm.projects.copilot.suggestion_changes_prompt',
  ),
  project_brief: definition(
    z.object({ projectId: id }).strict(),
    'crm.projects.copilot.suggestion_brief',
    'crm.projects.copilot.suggestion_brief_prompt',
  ),
  action_plan: definition(
    deal,
    'crm.chat.suggestion_action_plan',
    'crm.chat.suggestion_action_plan_prompt',
  ),
  create_plan_tasks: definition(
    selection,
    'crm.chat.action_plan.create_tasks',
    'crm.chat.action_plan.create_tasks',
  ),
  meeting_tasks: definition(
    selection,
    'crm.chat.meeting_outcome.create_selected_tasks',
    'crm.chat.meeting_outcome.create_tasks_command',
  ),
  meeting_followup: definition(
    z.object({ dealId: id, sourceMessageId: id }).strict(),
    'crm.chat.meeting_outcome.prepare_follow_up',
    'crm.chat.meeting_outcome.prepare_followup_command',
  ),
  meeting_send: definition(
    z.object({ dealId: id, sourceMessageId: id }).strict(),
    'crm.chat.email_draft.send_for_approval',
    'crm.chat.email_draft.send_approval_command',
  ),
  prepare_follow_up: definition(
    deal,
    'crm.chat.deal_risk.prepare_follow_up',
    'crm.features.dynamic.features.ai.chat.components.chatpanel.13',
  ),
  touch_strategy: definition(
    deal,
    'crm.chat.touch_strategy.cockpit_button',
    'crm.chat.touch_strategy.trigger',
  ),
  scenario_my_day: definition(
    empty,
    'crm.ai_chat.right_panel.scenario_my_day',
    'crm.ai_chat.prompt.my_day',
  ),
  scenario_follow_up: definition(
    entity,
    'crm.ai_chat.suggestions.follow_up',
    'crm.ai_chat.prompt.follow_up',
  ),
  scenario_daily_plan: definition(
    empty,
    'crm.ai_chat.suggestions.daily_tasks',
    'crm.ai_chat.prompt.daily_plan',
  ),
  extract_tasks_from_note: definition(
    z.object({ noteId: id }).strict(),
    'crm.ai_chat.suggestions.extract_tasks',
    'crm.ai_chat.prompt.extract_tasks',
  ),
  scenario_extract_tasks: definition(
    empty,
    'crm.ai_chat.suggestions.extract_tasks',
    'crm.ai_chat.prompt.extract_tasks',
  ),
  scenario_hot_leads: definition(
    z.union([empty, briefingSelection('dealIds')]),
    'crm.ai_chat.suggestions.hot_leads',
    'crm.ai_chat.prompt.hot_leads',
  ),
  scenario_deal_risks: definition(
    empty,
    'crm.ai_chat.suggestions.risk_audit',
    'crm.ai_chat.prompt.deal_risks',
  ),
  scenario_overdue_tasks: definition(
    briefingSelection('taskIds'),
    'crm.chat.manager_briefing.priority_overdue_tasks',
    'crm.ai_chat.prompt.my_day_overdue_tasks',
  ),
  scenario_risk_deals: definition(
    briefingSelection('dealIds'),
    'crm.chat.manager_briefing.priority_risk_deals',
    'crm.ai_chat.prompt.my_day_risk_deals',
  ),
  scenario_meetings: definition(
    z.union([
      z.object({ sourceMessageId: id, meetingIds: z.array(id).max(20) }).strict(),
      z
        .object({
          source: dashboardSource,
          meetingIds: z
            .array(id)
            .min(1)
            .max(20)
            .refine((values) => new Set(values).size === values.length),
        })
        .strict(),
    ]),
    'crm.chat.manager_briefing.priority_meetings',
    'crm.ai_chat.prompt.my_day_meetings',
  ),
  executive_brief: definition(
    empty,
    'crm.ai_chat.welcome.manager_brief_title',
    'crm.ai_chat.prompt.executive_brief',
  ),
  pipeline_audit: definition(
    empty,
    'crm.ai_chat.welcome.pipeline_audit_title',
    'crm.ai_chat.prompt.pipeline_audit',
  ),
} as const;

export type UiCommandId = keyof typeof UI_COMMAND_REGISTRY;
export interface UiCommandRequest {
  commandId: UiCommandId;
  params: Record<string, unknown>;
}
export type ChatTurnAction =
  | { message: string; commandId?: never; params?: never }
  | (UiCommandRequest & { message?: never });

export const uiCommandInputSchema = z
  .object({
    commandId: z.enum(Object.keys(UI_COMMAND_REGISTRY) as [UiCommandId, ...UiCommandId[]]),
    params: z.record(z.string(), z.unknown()),
  })
  .strict();
export const uiCommandSchema = uiCommandInputSchema.superRefine((request, context) => {
  const result = UI_COMMAND_REGISTRY[request.commandId].schema.safeParse(request.params);
  if (!result.success) {
    for (const issue of result.error.issues)
      context.addIssue({ ...issue, path: ['params', ...issue.path] });
  }
});

export function catalogText(
  locale: 'ru' | 'en',
  key: string,
  params: Record<string, unknown> = {},
): string {
  const text = commandCatalogs[locale ?? 'ru'][key.replace(/^crm\./u, '')];
  const template = typeof text === 'string' ? text : key;
  return template.replace(/\{(\w+)\}/gu, (match, name: string) =>
    typeof params[name] === 'string' ? (params[name] as string) : match,
  );
}

export function commandLabel(command: UiCommandRequest, locale: 'ru' | 'en'): string {
  return catalogText(
    locale,
    UI_COMMAND_REGISTRY[command.commandId].presentationKey,
    command.params,
  );
}

export function commandRequest(
  key: UiCommandKey,
  params: Record<string, unknown> = {},
): UiCommandRequest {
  return { commandId: UI_COMMANDS[key].id, params };
}

export function commandFromRefs(refs: readonly ChatContextRef[]): UiCommandRequest {
  const command = refs.find((ref) => ref.type === 'command');
  if (!command || !(command.id in UI_COMMAND_REGISTRY)) throw new Error('Unknown UI command');
  const params: Record<string, unknown> = {};
  const taskIds = refs
    .filter((ref) => ref.type === 'task')
    .slice(0, 7)
    .map((ref) => ref.id);
  const dealIds = refs
    .filter((ref) => ref.type === 'deal')
    .slice(0, 7)
    .map((ref) => ref.id);
  if (taskIds.length) params.taskIds = taskIds;
  if (dealIds.length) params.dealIds = dealIds;
  return { commandId: command.id as UiCommandId, params };
}

export function canonicalParams(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalParams);
  if (value !== null && typeof value === 'object')
    return Object.fromEntries(
      Object.entries(value)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, item]) => [key, canonicalParams(item)]),
    );
  return value;
}

/** Count the same unique references that the server attaches after resolving params. */
export function commandContextRefCount(
  command: UiCommandRequest,
  refs: readonly ChatContextRef[],
): number {
  const keys = new Set(
    refs.filter((ref) => ref.type !== 'command').map((ref) => `${ref.type}:${ref.id}`),
  );
  for (const [field, type] of [
    ['dealId', 'deal'],
    ['contactId', 'contact'],
    ['companyId', 'company'],
    ['taskId', 'task'],
    ['noteId', 'note'],
    ['taskIds', 'task'],
    ['dealIds', 'deal'],
  ] as const) {
    const value = command.params[field];
    const values = typeof value === 'string' ? [value] : Array.isArray(value) ? value : [];
    for (const id of values) if (typeof id === 'string') keys.add(`${type}:${id}`);
  }
  if (command.params.source !== undefined && Array.isArray(command.params.meetingIds)) {
    for (const id of command.params.meetingIds)
      if (typeof id === 'string') keys.add(`calendar_event:${id}`);
  }
  if (typeof command.params.sourceMessageId === 'string') {
    const type =
      command.commandId === 'crm_list_next_page'
        ? 'crm_list'
        : [
              'create_tasks_from_answer',
              'add_deal_note',
              'dashboard_widget_from_answer',
              'create_note_from_artifact',
              'create_tasks_from_artifact',
            ].includes(command.commandId)
          ? 'assistant_message'
          : undefined;
    if (type) keys.add(`${type}:${command.params.sourceMessageId}`);
  }
  return keys.size + 1;
}

export function commandClarificationKey(code: string | undefined): string | undefined {
  if (code?.startsWith('ui_command_clarification_undo_'))
    return `crm.approvals.undo_state.${code.slice('ui_command_clarification_undo_'.length)}`;
  if (code === 'ui_command_context_limit') return 'crm.ai_chat.context_chips.max_reached';
  if (!code?.startsWith('ui_command_clarification_')) return undefined;
  const field = code.slice('ui_command_clarification_'.length);
  if (field === 'dealId') return 'crm.ai_chat.handoff.clarify_deal';
  if (field === 'noteSelection') return 'crm.ai_chat.handoff.select_note';
  if (field === 'noteId') return 'crm.ai_chat.handoff.clarify_note';
  if (
    ['source', 'sourceMessageId', 'taskIds', 'dealIds', 'meetingIds', 'contextRefs'].includes(field)
  )
    return 'crm.ai_chat.handoff.clarify_selection';
  return 'crm.ai_chat.handoff.invalid_command';
}
